/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import { useEffect, type JSX } from 'react';

import { LinkNode } from '@lexical/link';
import {
  AutoLinkPlugin,
  createLinkMatcherWithRegExp,
  LinkMatcher,
} from '@lexical/react/LexicalAutoLinkPlugin';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';


const URL_REGEX =
  /((https?:\/\/(www\.)?)|(www\.))[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)(?<![-.+():%])/;

const EMAIL_REGEX =
  /(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))/;

const MATCHERS: Array<LinkMatcher> = [
  // 1.a. Matcher for URLs
  (text) => {
    // console.debug('Matching URL in text:', text);

    const match = URL_REGEX.exec(text);

    if (match === null) {
      return null;
    }

    const fullMatch = match[0];

    return {
      index: match.index,
      length: fullMatch.length,
      text: fullMatch,
      url: fullMatch.startsWith('http') ? fullMatch : `https://${fullMatch}`,
      attributes: { rel: 'noopener noreferrer', target: '_blank' },
    };
  },
  // // 1.b. Matcher for URLs
  // createLinkMatcherWithRegExp(URL_REGEX, (text) => {
  //   return text.startsWith('http') ? text : `https://${text}`;
  // }),

  // 2. Matcher for email addresses
  createLinkMatcherWithRegExp(EMAIL_REGEX, (text) => {
    return `mailto:${text}`;
  }),
];

export function LexicalAutoLinkPlugin(): JSX.Element {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    // This listens for any time a LinkNode is created or changed
    return editor.registerNodeTransform(LinkNode, (node: LinkNode) => {
      const nodeTarget = node.getTarget();

      /** 
       * We check for undefined because if the user explicitly sets target to a value, we don't want to override that
       * and that's how we can detect pasted links 
      */
      if (!nodeTarget) {
        node.setTarget('_blank');
        node.setRel('noopener noreferrer');
      }

    });
  }, [editor]);

  return <AutoLinkPlugin matchers={MATCHERS} />;
}
